import axiosConfig from '../../../../Config/axios';
import {getSystemCoreRoute} from '../../../../Route/RouteIndex';
import type {Multimedia, MultimediaListResult} from '../Types/MultimediaTypes';

type MultimediaQueryParams = Record<string, unknown>;
type MultimediaRelation = Multimedia | Multimedia[] | null | undefined;

const MULTIMEDIA_ROUTE = getSystemCoreRoute('multimedia');

const getResponsePayload = (response: any) => response?.data?.data;

const isObject = (value: unknown): value is Record<string, any> => {
    return typeof value === 'object' && value !== null;
};

const isAbsoluteUrl = (value?: string): boolean => {
    if (!value) return false;
    return /^https?:\/\//i.test(value);
};

const getApiBaseOrigin = (): string => {
    const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

    if (isAbsoluteUrl(configuredBaseUrl)) {
        try {
            return new URL(configuredBaseUrl).origin;
        } catch (_exception) {
            return '';
        }
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }

    return '';
};

const toAbsoluteUrl = (value?: string): string => {
    if (!value) return '';
    if (isAbsoluteUrl(value)) return value;

    const baseOrigin = getApiBaseOrigin();
    if (!baseOrigin) return value;

    const normalizedPath = value.startsWith('/') ? value : `/${value}`;
    return `${baseOrigin}${normalizedPath}`;
};

const normalizeMultimedia = (payload: unknown): Multimedia | null => {
    if (!isObject(payload)) return null;

    if (typeof payload.id !== 'number') {
        return null;
    }

    const normalizedPayload = payload as Multimedia;
    return {
        ...normalizedPayload,
    };
};

const normalizeMultimediaRelation = (payload: MultimediaRelation | unknown): Multimedia | null => {
    if (Array.isArray(payload)) {
        if (!payload.length) return null;
        return normalizeMultimedia(payload[0]);
    }

    return normalizeMultimedia(payload);
};

const resolveMultimediaUrl = (input?: MultimediaRelation): string => {
    const multimedia = normalizeMultimediaRelation(input);
    if (!multimedia) return '';

    const explicitUrl = multimedia.public_url || multimedia.url || multimedia.view_url || multimedia.download_url;
    if (explicitUrl) {
        return toAbsoluteUrl(explicitUrl);
    }

    if (multimedia.path) {
        const pathUrl = multimedia.path.startsWith('/') ? multimedia.path : `/storage/${multimedia.path}`;
        return toAbsoluteUrl(pathUrl);
    }

    return '';
};

const normalizeListResponse = (payload: any): MultimediaListResult => {
    if (Array.isArray(payload)) {
        return {
            data: payload
                .map((item) => normalizeMultimedia(item))
                .filter((item): item is Multimedia => Boolean(item)),
        };
    }

    if (Array.isArray(payload?.data)) {
        return {
            data: payload.data
                .map((item: unknown) => normalizeMultimedia(item))
                .filter((item: Multimedia | null): item is Multimedia => Boolean(item)),
            meta: payload.meta,
        };
    }

    return {
        data: [],
        meta: payload?.meta,
    };
};

const resolveFileName = (multimedia?: Multimedia | null): string => {
    if (!multimedia) return 'download';

    return multimedia.original_name
        || multimedia.file_name
        || multimedia.title
        || multimedia.name
        || `multimedia-${multimedia.id}`;
};

const resolveViewEndpoint = (multimedia: Multimedia): string => {
    const explicitUrl = resolveMultimediaUrl(multimedia);
    if (explicitUrl) return explicitUrl;
    if (multimedia.id) return `${MULTIMEDIA_ROUTE}/${multimedia.id}/view`;
    return '';
};

const resolveDownloadEndpoint = (multimedia: Multimedia): string => {
    const explicitUrl = resolveMultimediaUrl(multimedia);
    if (explicitUrl) return explicitUrl;
    if (multimedia.id) return `${MULTIMEDIA_ROUTE}/${multimedia.id}/download`;
    return '';
};

const openBlobInNewTab = (blob: Blob, fileName: string) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

    if (!newWindow) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
    }, 60000);
};

const downloadBlob = (blob: Blob, fileName: string) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
    }, 60000);
};

const openUrlInNewTab = (url: string) => {
    const target = toAbsoluteUrl(url);
    if (!target) return;
    window.open(target, '_blank', 'noopener,noreferrer');
};

const downloadUrlDirectly = (url: string, fileName: string) => {
    const target = toAbsoluteUrl(url);
    if (!target) return;

    const link = document.createElement('a');
    link.href = target;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const MultimediaService = {
    fetchMultimedia: async (params: MultimediaQueryParams = {}): Promise<MultimediaListResult> => {
        const response = await axiosConfig.get(MULTIMEDIA_ROUTE, {params});
        return normalizeListResponse(getResponsePayload(response));
    },

    fetchMultimediaItem: async (id: number): Promise<Multimedia | null> => {
        const response = await axiosConfig.get(`${MULTIMEDIA_ROUTE}/${id}`);
        return normalizeMultimediaRelation(getResponsePayload(response));
    },

    uploadMultimedia: async (file: File, data: MultimediaQueryParams = {}): Promise<Multimedia> => {
        const formData = new FormData();
        formData.append('file', file);
        Object.entries(data).forEach(([key, value]) => {
            if (typeof value === 'undefined' || value === null) return;
            formData.append(key, String(value));
        });

        const response = await axiosConfig.post(MULTIMEDIA_ROUTE, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        const normalizedMultimedia = normalizeMultimediaRelation(getResponsePayload(response));
        if (!normalizedMultimedia) {
            throw new Error('Unable to normalize multimedia upload response.');
        }

        return normalizedMultimedia;
    },

    deleteMultimedia: async (id: number): Promise<Multimedia | null> => {
        const response = await axiosConfig.delete(`${MULTIMEDIA_ROUTE}/${id}`);
        return normalizeMultimediaRelation(getResponsePayload(response));
    },

    viewMultimedia: async (multimedia: Multimedia): Promise<void> => {
        const endpoint = resolveViewEndpoint(multimedia);
        const directUrl = resolveMultimediaUrl(multimedia);

        if (!endpoint) {
            throw new Error('Unable to resolve multimedia view endpoint.');
        }

        if (directUrl && endpoint === directUrl) {
            openUrlInNewTab(directUrl);
            return;
        }

        const response = await axiosConfig.get(endpoint, {
            responseType: 'blob',
        });

        openBlobInNewTab(response.data, resolveFileName(multimedia));
    },

    downloadMultimedia: async (multimedia: Multimedia): Promise<void> => {
        const endpoint = resolveDownloadEndpoint(multimedia);
        const directUrl = resolveMultimediaUrl(multimedia);

        if (!endpoint) {
            throw new Error('Unable to resolve multimedia download endpoint.');
        }

        if (directUrl && endpoint === directUrl) {
            downloadUrlDirectly(directUrl, resolveFileName(multimedia));
            return;
        }

        const response = await axiosConfig.get(endpoint, {
            responseType: 'blob',
        });

        downloadBlob(response.data, resolveFileName(multimedia));
    },

    normalizeMultimedia,
    normalizeMultimediaRelation,
    resolveMultimediaUrl,
    openUrlInNewTab,
    downloadUrlDirectly,
    toAbsoluteUrl,
    resolveFileName,
    resolveViewEndpoint,
    resolveDownloadEndpoint,
};

export default MultimediaService;





